import { ArrowPathIcon, ArrowUturnLeftIcon, CheckCircleIcon, CheckIcon, CloudArrowUpIcon, ExclamationTriangleIcon, LockClosedIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "src/async/apiUtils";
import FeedbackButton, { useFlash } from "src/components/ServerMonitoring/FeedbackButton";
import Checkbox from "src/components/Shared/Forms/Inputs/Checkbox";
import Modal from "src/components/Shared/Modal";
import { classNames } from "src/helpers/classNames";
import { formatDateTime, formatRelative } from "src/helpers/serverMonitorHelper";

const phaseLabel = (phase) => (phase ? phase.charAt(0) + phase.slice(1).toLowerCase().replace(/_/g, " ") : "Unknown");

const Warning = ({ children }) => (
  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
    <div>{children}</div>
  </div>
);

// Runs one POST action from a confirmation popup. Success closes the popup (the triggering button confirms in place);
// a failure is shown inside the popup, which stays open so the admin can retry or cancel. No toasts.
const useConfirmAction = (isOpen, onDone) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen]);

  const run = async (url, body = {}) => {
    setLoading(true);
    setError(null);
    try {
      await apiRequest("post", url, { body });
      setLoading(false);
      onDone();
    } catch (err) {
      setLoading(false);
      setError(err.message || "The action failed.");
    }
  };
  return { loading, error, run };
};

const ActionError = ({ message }) =>
  message ? (
    <motion.div
      initial={{ opacity: 0, x: 0 }}
      animate={{ opacity: 1, x: [0, -6, 6, -4, 4, 0] }}
      transition={{ duration: 0.4 }}
      role="alert"
      className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      <XCircleIcon className="h-5 w-5 flex-shrink-0" />
      <div>{message}</div>
    </motion.div>
  ) : null;

const RestartModal = ({ isOpen, onClose, onDone, componentName }) => {
  const { loading, error, run } = useConfirmAction(isOpen, onDone);
  const confirm = () => run("/server-monitor/actions/restart");
  return (
    <Modal
      title="Restart Node server"
      isOpen={isOpen}
      onCancel={onClose}
      onSuccess={confirm}
      isLoading={loading}
      defaultOptions={{ onSuccessButtonText: "Restart", onSuccessLoaderVisible: true }}>
      <div className="space-y-3 pb-2 text-sm text-gray-700">
        <ActionError message={error} />
        <p>
          DigitalOcean will do a rolling restart of the <b>{componentName}</b> component using its <b>current build</b>. No code is pulled or rebuilt. This usually takes 1–2 minutes.
        </p>
        <p>Use this for a runtime crash (a bad request, out of memory) or when files were changed inside the running container.</p>
        <Warning>If the crash is caused by a bug in the code, the restarted server will crash again. Use Roll back instead.</Warning>
      </div>
    </Modal>
  );
};

const RedeployModal = ({ isOpen, onClose, onDone, source }) => {
  const { loading, error, run } = useConfirmAction(isOpen, onDone);
  const [forceBuild, setForceBuild] = useState(false);
  useEffect(() => {
    if (isOpen) setForceBuild(false);
  }, [isOpen]);

  const confirm = () => run("/server-monitor/actions/redeploy", { force_build: forceBuild });
  return (
    <Modal
      title="Redeploy app"
      isOpen={isOpen}
      onCancel={onClose}
      onSuccess={confirm}
      isLoading={loading}
      defaultOptions={{ onSuccessButtonText: "Redeploy", onSuccessLoaderVisible: true }}>
      <div className="space-y-3 pb-2 text-sm text-gray-700">
        <ActionError message={error} />
        <p>DigitalOcean will pull the latest code, build it and deploy it. This takes a few minutes.</p>
        {source ? (
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <div>
              <span className="text-gray-500">Repository:</span> <b className="break-all">{source.repo || "—"}</b>
            </div>
            <div>
              <span className="text-gray-500">{source.type === "image" ? "Tag" : "Branch"}:</span> <b>{source.branch || "—"}</b>
            </div>
            <div className="mt-1 text-xs text-gray-500">The latest commit on this branch will be deployed - make sure your fix has been pushed.</div>
          </div>
        ) : (
          <p className="text-gray-500">The latest commit on the app&apos;s configured branch will be deployed - make sure your fix has been pushed.</p>
        )}
        <Checkbox
          isChecked={forceBuild}
          onChange={(e) => setForceBuild(e.target.checked)}
          checkboxLabel="Rebuild from scratch (ignore the cached build - slower, helps with dependency problems)"
        />
        <p className="text-xs text-gray-500">If the new code cannot start, DigitalOcean marks the deployment as failed and keeps the current version running (when an HTTP health check is configured).</p>
      </div>
    </Modal>
  );
};

const RollbackModal = ({ isOpen, onClose, onDone, candidates = [] }) => {
  const [selected, setSelected] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const { loading, error, run } = useConfirmAction(isOpen, onDone);

  const validate = useCallback(async (deploymentId) => {
    setSelected(deploymentId);
    setValidation(null);
    if (!deploymentId) return;
    setValidating(true);
    try {
      const res = await apiRequest("post", "/server-monitor/actions/rollback/validate", { body: { deployment_id: deploymentId } });
      setValidation(res.data.data);
    } catch (error) {
      setValidation({ valid: false, error: { message: error.message } });
    } finally {
      setValidating(false);
    }
  }, []);

  // Suggest the most recent deployment that went live before the current one.
  useEffect(() => {
    if (isOpen) validate(candidates[0]?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const confirm = () => run("/server-monitor/actions/rollback", { deployment_id: selected });

  return (
    <Modal
      title="Roll back to a previous deployment"
      isOpen={isOpen}
      size="lg"
      onCancel={onClose}
      onSuccess={confirm}
      isLoading={loading}
      defaultOptions={{ onSuccessButtonText: "Roll back", onSuccessLoaderVisible: true, onSuccessButtonDisabled: !selected || validating || !validation?.valid }}>
      <div className="space-y-3 pb-2 text-sm text-gray-700">
        <ActionError message={error} />
        <p>Users go back to a version that worked, while the bug is fixed. DigitalOcean redeploys the old build, so it is quick.</p>
        {candidates.length === 0 ? (
          <p className="rounded-md bg-gray-50 px-3 py-4 text-gray-500">There are no earlier deployments that went live.</p>
        ) : (
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {candidates.map((deployment, index) => (
              <label
                key={deployment.id}
                className={classNames("flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2", selected === deployment.id ? "border-highlightColor bg-gray-50" : "border-gray-200 hover:bg-gray-50")}>
                <input
                  type="radio"
                  name="rollback-deployment"
                  className="mt-1 text-highlightColor focus:ring-0"
                  checked={selected === deployment.id}
                  onChange={() => validate(deployment.id)}
                />
                <div className="min-w-0">
                  <div className="font-medium text-gray-900">
                    {formatDateTime(deployment.created_at, "MMM D, YYYY h:mm A")}
                    {index === 0 && <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs font-normal text-green-700">Suggested - last working version</span>}
                  </div>
                  <div className="truncate text-xs text-gray-500">{deployment.cause || deployment.id}</div>
                </div>
              </label>
            ))}
          </div>
        )}
        {validating && <p className="text-xs text-gray-500">Checking with DigitalOcean whether this rollback is possible…</p>}
        {validation && !validation.valid && <Warning>{validation.error?.message || "DigitalOcean cannot roll back to this deployment."}</Warning>}
        {validation?.valid && (
          <>
            <p className="flex items-center gap-1 text-xs text-green-700">
              <CheckCircleIcon className="h-4 w-4" /> DigitalOcean confirmed this rollback is possible.
            </p>
            {validation.warnings?.map((warning) => (
              <Warning key={warning.code}>{warning.message}</Warning>
            ))}
          </>
        )}
        <Warning>
          After a rollback, DigitalOcean <b>pauses all new deployments</b> (including auto-deploy on push) until you choose <b>Revert rollback</b> (deploy your latest code again) or <b>Keep this version</b>.
        </Warning>
      </div>
    </Modal>
  );
};

const ConfirmModal = ({ isOpen, onClose, onDone, title, url, buttonText, children }) => {
  const { loading, error, run } = useConfirmAction(isOpen, onDone);
  const confirm = () => run(url);
  return (
    <Modal
      title={title}
      isOpen={isOpen}
      onCancel={onClose}
      onSuccess={confirm}
      isLoading={loading}
      defaultOptions={{ onSuccessButtonText: buttonText, onSuccessLoaderVisible: true }}>
      <div className="space-y-3 pb-2 text-sm text-gray-700">
        <ActionError message={error} />
        {children}
      </div>
    </Modal>
  );
};

// Restart / Redeploy / Roll back controls for the Node server (Super Admins; the DigitalOcean token needs app:update).
const ServerActions = ({ status, onChanged }) => {
  const [actions, setActions] = useState(null);
  const [open, setOpen] = useState(null);
  const [flashKey, flash] = useFlash();

  const app = status?.app;
  const inProgress = app?.in_progress_deployment;
  const pinned = app?.pinned_deployment;

  const loadActions = useCallback(async () => {
    try {
      const res = await apiRequest("get", "/server-monitor/actions");
      setActions(res.data.data);
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (status?.actions_enabled) loadActions();
  }, [status?.actions_enabled, inProgress?.id, pinned?.id, loadActions]);

  const done = (key) => () => {
    setOpen(null);
    flash(key);
    onChanged();
    loadActions();
  };

  if (!status?.configured) return null;
  if (!status?.actions_enabled) {
    return null;
  }

  const busy = !!inProgress;
  const disabledReason = busy ? `A deployment is in progress (${phaseLabel(inProgress.phase)})` : pinned ? "The app is locked to a rollback" : null;
  const lastAction = actions?.last_action || status?.last_action;

  return (
    <div className="mt-5 border-t border-gray-100 pt-4">
      {pinned && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <LockClosedIcon className="h-5 w-5 flex-shrink-0" />
            <div>
              <p>
                <b>Rolled back</b> to the deployment from {formatDateTime(pinned.created_at, "MMM D, YYYY h:mm A")}
                {pinned.cause ? ` (${pinned.cause})` : ""}. <b>New deployments are paused</b>, including auto-deploy on push.
              </p>
              <p className="mt-1 text-xs">Once your fix is pushed, choose Revert rollback to deploy the latest code. Choose Keep this version to stay on it permanently.</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <FeedbackButton
              version="secondary"
              type="button"
              disabled={busy}
              done={flashKey === "revert"}
              doneLabel="Rollback reverted"
              onClick={() => setOpen("revert")}>
              <CloudArrowUpIcon className="h-4 w-4" /> Revert rollback
            </FeedbackButton>
            <FeedbackButton
              version="gray"
              type="button"
              disabled={busy}
              done={flashKey === "commit"}
              doneLabel="Version kept"
              onClick={() => setOpen("commit")}>
              <CheckCircleIcon className="h-4 w-4" /> Keep this version
            </FeedbackButton>
          </div>
        </div>
      )}

      <AnimatePresence>
        {!pinned && ["revert", "commit"].includes(flashKey) && (
          <motion.div
            key="rollback-done"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-4 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
            <motion.span
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: [0, 1.35, 1], rotate: 0 }}
              transition={{ duration: 0.35 }}>
              <CheckIcon className="h-4 w-4 stroke-[2.5]" />
            </motion.span>
            {flashKey === "revert" ? "Rollback reverted - the latest code is being deployed and deployments are resumed." : "Version kept - deployments are resumed."}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-wrap items-center gap-2">
        <FeedbackButton
          version="secondary"
          type="button"
          disabled={!!disabledReason}
          done={flashKey === "restart"}
          doneLabel="Restart started"
          onClick={() => setOpen("restart")}>
          <ArrowPathIcon className="h-4 w-4" /> Restart
        </FeedbackButton>
        <FeedbackButton
          version="secondary"
          type="button"
          disabled={!!disabledReason}
          done={flashKey === "redeploy"}
          doneLabel="Redeploy started"
          onClick={() => setOpen("redeploy")}>
          <CloudArrowUpIcon className="h-4 w-4" /> Redeploy
        </FeedbackButton>
        <FeedbackButton
          version="secondary"
          type="button"
          disabled={!!disabledReason || !actions}
          done={flashKey === "rollback"}
          doneLabel="Rollback started"
          onClick={() => setOpen("rollback")}>
          <ArrowUturnLeftIcon className="h-4 w-4" /> Roll back
        </FeedbackButton>
        {disabledReason && <span className="text-xs text-gray-500">{disabledReason}.</span>}
      </div>
      {lastAction && (
        <p className="mt-2 text-xs text-gray-400">
          Last action: {lastAction.label} by {lastAction.by_name}, {formatRelative(lastAction.at)}.
        </p>
      )}

      <RestartModal
        isOpen={open === "restart"}
        onClose={() => setOpen(null)}
        onDone={done("restart")}
        componentName={status?.component_name}
      />
      <RedeployModal
        isOpen={open === "redeploy"}
        onClose={() => setOpen(null)}
        onDone={done("redeploy")}
        source={actions?.source || app?.component_source}
      />
      <RollbackModal
        isOpen={open === "rollback"}
        onClose={() => setOpen(null)}
        onDone={done("rollback")}
        candidates={actions?.rollback_candidates || []}
      />
      <ConfirmModal
        isOpen={open === "revert"}
        onClose={() => setOpen(null)}
        onDone={done("revert")}
        title="Revert rollback"
        url="/server-monitor/actions/rollback/revert"
        buttonText="Revert rollback">
        <p>DigitalOcean deploys the latest version of the app again and resumes new deployments (including auto-deploy on push).</p>
        <Warning>Only do this once the fix has been pushed - otherwise the broken version goes live again.</Warning>
      </ConfirmModal>
      <ConfirmModal
        isOpen={open === "commit"}
        onClose={() => setOpen(null)}
        onDone={done("commit")}
        title="Keep this version"
        url="/server-monitor/actions/rollback/commit"
        buttonText="Keep this version">
        <p>The rolled-back version becomes the permanent version and new deployments resume. The next push (auto-deploy) or Redeploy will deploy the latest code.</p>
      </ConfirmModal>
    </div>
  );
};

export default ServerActions;
